import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Button as TamaguiButton,
  Checkbox as TamaguiCheckbox,
  GetProps,
  Input as TamaguiInput,
  Spinner,
  Text as TamaguiText,
  TextArea as TamaguiTextArea,
  XStack,
  YStack,
  styled,
} from 'tamagui';

import { kindredFontFamilies } from './tokens';

const styledUnsafe = styled as any;
const UnsafeButton = TamaguiButton as any;
const UnsafeCheckbox = TamaguiCheckbox as any;
const UnsafeInput = TamaguiInput as any;
const UnsafeText = TamaguiText as any;
const UnsafeTextArea = TamaguiTextArea as any;
const UnsafeXStack = XStack as any;
const UnsafeYStack = YStack as any;

const isWebEnvironment =
  typeof document !== 'undefined' && typeof window !== 'undefined' && typeof navigator !== 'undefined';

const resolveKindredFont = (
  fontFamily: GetProps<typeof TamaguiText>['fontFamily'],
  fontWeight?: GetProps<typeof TamaguiText>['fontWeight'],
) => {
  if (!isWebEnvironment || typeof fontFamily !== 'string') {
    return { fontFamily, fontWeight };
  }

  if (fontFamily === kindredFontFamilies.display) {
    return {
      fontFamily: 'Manrope' as GetProps<typeof TamaguiText>['fontFamily'],
      fontWeight: fontWeight ?? ('600' as GetProps<typeof TamaguiText>['fontWeight']),
    };
  }

  if (fontFamily === kindredFontFamilies.displayBold) {
    return {
      fontFamily: 'Manrope' as GetProps<typeof TamaguiText>['fontFamily'],
      fontWeight: fontWeight ?? ('700' as GetProps<typeof TamaguiText>['fontWeight']),
    };
  }

  if (fontFamily === kindredFontFamilies.sansMedium) {
    return {
      fontFamily: 'Manrope' as GetProps<typeof TamaguiText>['fontFamily'],
      fontWeight: fontWeight ?? ('500' as GetProps<typeof TamaguiText>['fontWeight']),
    };
  }

  if (fontFamily === kindredFontFamilies.sansSemi) {
    return {
      fontFamily: 'Manrope' as GetProps<typeof TamaguiText>['fontFamily'],
      fontWeight: fontWeight ?? ('600' as GetProps<typeof TamaguiText>['fontWeight']),
    };
  }

  if (fontFamily === kindredFontFamilies.sansBold) {
    return {
      fontFamily: 'Manrope' as GetProps<typeof TamaguiText>['fontFamily'],
      fontWeight: fontWeight ?? ('700' as GetProps<typeof TamaguiText>['fontWeight']),
    };
  }

  return { fontFamily, fontWeight };
};

export const AppPage = styledUnsafe(YStack, {
  name: 'AppPage',
  width: '100%',
  maxWidth: 1152,
  alignSelf: 'center',
  px: '$sm',
  pt: '$xs',
  pb: '$lg',
  gap: 9,
  $gtSm: {
    px: '$lg',
    pt: 10,
    pb: '$xl',
  },
});

export const AppSection = styledUnsafe(YStack, {
  name: 'AppSection',
  width: '100%',
  gap: 9,
});

export const AppRow = styledUnsafe(XStack, {
  name: 'AppRow',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '$sm',
});

export function AppText({
  fontFamily = kindredFontFamilies.sans,
  fontWeight,
  color = '$color',
  fontSize = '$md',
  lineHeight = 20,
  ...props
}: GetProps<typeof TamaguiText>) {
  const resolvedFont = resolveKindredFont(fontFamily, fontWeight);

  return (
    <UnsafeText
      color={color}
      fontSize={fontSize}
      lineHeight={lineHeight}
      fontFamily={resolvedFont.fontFamily}
      fontWeight={resolvedFont.fontWeight}
      {...props}
    />
  );
}

export const AppHeading = styledUnsafe(TamaguiText, {
  name: 'AppHeading',
  margin: 0,
  color: '$heading',
  fontFamily: isWebEnvironment ? 'Manrope' : kindredFontFamilies.displayBold,
  fontWeight: isWebEnvironment ? '700' : undefined,
  fontSize: '$display',
  lineHeight: '$display',
  letterSpacing: '$md',
  $gtSm: {
    fontSize: '$heroLg',
    lineHeight: '$heroLg',
    letterSpacing: '$lg',
  },
});

export const AppCard = styledUnsafe(YStack, {
  name: 'AppCard',
  backgroundColor: '$cardBackground',
  borderColor: '$borderColor',
  borderWidth: 1,
  borderRadius: '$lg',
  p: 0,
  gap: 0,
  overflow: 'hidden',
  shadowColor: '$shadowColor',
  shadowOffset: { width: 0, height: 10 },
  shadowOpacity: 0.05,
  shadowRadius: 22,
  elevation: 1,
});

export const AppCardHeader = styledUnsafe(YStack, {
  name: 'AppCardHeader',
  px: 14,
  pt: 10,
  pb: 6,
  gap: 6,
});

export const AppCardBody = styledUnsafe(YStack, {
  name: 'AppCardBody',
  px: 14,
  py: 10,
  gap: 10,
});

export const AppCardFooter = styledUnsafe(XStack, {
  name: 'AppCardFooter',
  px: 14,
  pt: 6,
  pb: 10,
  gap: 10,
  flexWrap: 'wrap',
  alignItems: 'center',
});

type AppButtonTone = 'primary' | 'secondary' | 'outline' | 'outlinePrimary' | 'outlineSecondary' | 'ghost' | 'danger';
type AppButtonSize = 'xs' | 'sm' | 'md' | 'lg';

type AppButtonProps = Omit<GetProps<typeof TamaguiButton>, 'size'> & {
  tone?: AppButtonTone;
  size?: AppButtonSize;
  children: ReactNode;
};

const BUTTON_TONES: Record<AppButtonTone, Partial<GetProps<typeof TamaguiButton>>> = {
  primary: {
    backgroundColor: '$primary',
    borderColor: '$primary',
    color: '$primaryForeground',
  },
  secondary: {
    backgroundColor: '$secondary',
    borderColor: '$secondary',
    color: '$secondaryForeground',
  },
  outline: {
    backgroundColor: '$cardBackground',
    borderColor: '$borderColor',
    color: '$color',
  },
  outlinePrimary: {
    backgroundColor: '$cardBackground',
    borderColor: '$primary',
    color: '$primary',
  },
  outlineSecondary: {
    backgroundColor: '$cardBackground',
    borderColor: '$secondary',
    color: '$secondary',
  },
  ghost: {
    backgroundColor: '$primarySoft',
    borderColor: '$primarySoft',
    color: '$primary',
  },
  danger: {
    backgroundColor: '$destructive',
    borderColor: '$destructive',
    color: '$destructiveForeground',
  },
};

const BUTTON_SIZES: Record<AppButtonSize, Record<string, unknown>> = {
  xs: {
    height: 22,
    px: 8,
    fontSize: '$xs',
  },
  sm: {
    height: 40,
    px: '$sm',
  },
  md: {
    height: 44,
    px: '$md',
  },
  lg: {
    height: 52,
    px: '$lg',
  },
};

export function AppButton({
  tone = 'primary',
  size = 'md',
  children,
  disabled,
  accessibilityRole = 'button',
  accessibilityState,
  ...props
}: AppButtonProps) {
  const resolvedFont = resolveKindredFont(kindredFontFamilies.sansBold, undefined);

  return (
    <UnsafeButton
      unstyled
      accessibilityRole={accessibilityRole}
      accessibilityState={{
        ...accessibilityState,
        disabled: Boolean(disabled || accessibilityState?.disabled),
      }}
      alignItems="center"
      justifyContent="center"
      flexDirection="row"
      gap="$xs"
      borderWidth={1}
      borderRadius="$pill"
      pressStyle={{ opacity: 0.92 }}
      disabledStyle={{ opacity: 0.52 }}
      fontFamily={resolvedFont.fontFamily}
      fontWeight={resolvedFont.fontWeight}
      fontSize="$sm"
      {...BUTTON_SIZES[size]}
      {...BUTTON_TONES[tone]}
      disabled={disabled}
      {...props}
    >
      {children}
    </UnsafeButton>
  );
}

type AppIconButtonProps = Omit<GetProps<typeof TamaguiButton>, 'size' | 'children'> & {
  tone?: AppButtonTone;
  size?: 'sm' | 'md';
  label: string;
  children: ReactNode;
};

const ICON_BUTTON_SIZES: Record<'sm' | 'md', { width: number; height: number }> = {
  sm: {
    width: 36,
    height: 36,
  },
  md: {
    width: 40,
    height: 40,
  },
};

export function AppIconButton({
  tone = 'outline',
  size = 'md',
  label,
  children,
  disabled,
  accessibilityRole = 'button',
  accessibilityState,
  ...props
}: AppIconButtonProps) {
  return (
    <UnsafeButton
      unstyled
      type="button"
      accessibilityLabel={label}
      accessibilityRole={accessibilityRole}
      accessibilityState={{
        ...accessibilityState,
        disabled: Boolean(disabled || accessibilityState?.disabled),
      }}
      alignItems="center"
      justifyContent="center"
      borderWidth={1}
      borderRadius="$pill"
      pressStyle={{ opacity: 0.92 }}
      disabledStyle={{ opacity: 0.52 }}
      aria-label={label}
      {...ICON_BUTTON_SIZES[size]}
      {...BUTTON_TONES[tone]}
      disabled={disabled}
      {...props}
    >
      {children}
    </UnsafeButton>
  );
}

export const AppButtonRow = styledUnsafe(XStack, {
  name: 'AppButtonRow',
  width: '100%',
  gap: 10,
  flexWrap: 'wrap',
  alignItems: 'center',
});

type AppSegmentedOption = {
  value: string;
  label: ReactNode;
  detail?: ReactNode;
};

type AppSegmentedControlProps = {
  options: AppSegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  pillItems?: boolean;
  wrapItems?: boolean;
};

export function AppSegmentedControl({
  options,
  value,
  onChange,
  pillItems = false,
  wrapItems = false,
}: AppSegmentedControlProps) {
  return (
    <UnsafeXStack
      width="100%"
      gap={4}
      p={4}
      borderRadius="$lg"
      backgroundColor="$panelSoftBackground"
      borderWidth={1}
      borderColor="$borderColor"
      flexWrap={wrapItems ? 'wrap' : 'nowrap'}
    >
      {options.map((option) => {
        const active = option.value === value;
        const optionLabel = typeof option.label === 'string' ? option.label : option.value;
        const optionDetail =
          typeof option.detail === 'string' || typeof option.detail === 'number'
            ? String(option.detail)
            : null;
        const accessibilityLabel = optionDetail ? `${optionLabel}, ${optionDetail}` : optionLabel;

        return (
          <UnsafeButton
            key={option.value}
            unstyled
            accessibilityLabel={accessibilityLabel}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            flexBasis={wrapItems ? 'calc(50% - 2px)' : 0}
            flexGrow={wrapItems ? 0 : 1}
            flexShrink={1}
            minWidth={wrapItems ? 'calc(50% - 2px)' : 0}
            minHeight={46}
            px="$xs"
            py="$xs"
            borderRadius={pillItems ? "$pill" : "$md"}
            alignItems="center"
            justifyContent="center"
            borderWidth={1}
            borderColor={active ? '$borderStrong' : 'transparent'}
            backgroundColor={active ? '$primary' : 'transparent'}
            pressStyle={{ opacity: 0.92 }}
            onPress={() => onChange(option.value)}
          >
            <UnsafeYStack alignItems="center" gap={2}>
              <AppText
                fontFamily={kindredFontFamilies.sansBold}
                fontSize={10}
                lineHeight={12}
                textTransform="uppercase"
                letterSpacing={1.2}
                color={active ? '$primaryForeground' : '$colorMuted'}
              >
                {option.label}
              </AppText>
              {option.detail ? (
                <AppText
                  fontFamily={kindredFontFamilies.sansBold}
                  fontSize="$md"
                  lineHeight="$md"
                  color={active ? '$primaryForeground' : '$color'}
                >
                  {option.detail}
                </AppText>
              ) : null}
            </UnsafeYStack>
          </UnsafeButton>
        );
      })}
    </UnsafeXStack>
  );
}

export function AppInput(props: GetProps<typeof TamaguiInput>) {
  const resolvedFont = resolveKindredFont(props.fontFamily ?? kindredFontFamilies.sans, props.fontWeight);

  return (
    <UnsafeInput
      height={46}
      px="$md"
      py="$sm"
      backgroundColor="$inputBackground"
      borderColor="$inputBorderColor"
      borderWidth={1}
      borderRadius="$md"
      color="$color"
      fontFamily={resolvedFont.fontFamily}
      fontWeight={resolvedFont.fontWeight}
      fontSize="$md"
      focusStyle={{ borderColor: '$primary' }}
      {...props}
    />
  );
}

export function AppTextArea(props: GetProps<typeof TamaguiTextArea>) {
  const resolvedFont = resolveKindredFont(props.fontFamily ?? kindredFontFamilies.sans, props.fontWeight);

  return (
    <UnsafeTextArea
      minHeight={124}
      px="$md"
      py="$sm"
      backgroundColor="$inputBackground"
      borderColor="$inputBorderColor"
      borderWidth={1}
      borderRadius="$md"
      color="$color"
      fontFamily={resolvedFont.fontFamily}
      fontWeight={resolvedFont.fontWeight}
      fontSize="$md"
      focusStyle={{ borderColor: '$primary' }}
      {...props}
    />
  );
}

type AppCheckboxProps = Omit<GetProps<typeof TamaguiCheckbox>, 'children' | 'checked' | 'onCheckedChange'> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
};

export function AppCheckbox({
  checked,
  onCheckedChange,
  label,
  description,
  accessibilityLabel,
  accessibilityState,
  ...props
}: AppCheckboxProps) {
  const resolvedAccessibilityLabel = accessibilityLabel ?? (typeof label === 'string' ? label : undefined);

  return (
    <UnsafeXStack alignItems="flex-start" gap="$sm">
      <UnsafeCheckbox
        accessibilityLabel={resolvedAccessibilityLabel}
        accessibilityRole="checkbox"
        accessibilityState={{ ...accessibilityState, checked }}
        checked={checked}
        onCheckedChange={(nextValue: unknown) => onCheckedChange(Boolean(nextValue))}
        width={20}
        height={20}
        minWidth={20}
        borderWidth={1}
        borderRadius="$sm"
        alignItems="center"
        justifyContent="center"
        backgroundColor={checked ? '$primary' : '$inputBackground'}
        borderColor={checked ? '$primary' : '$inputBorderColor'}
        pressStyle={{ opacity: 0.92 }}
        mt={2}
        {...props}
      >
        <UnsafeCheckbox.Indicator>
          <AppText
            color="$primaryForeground"
            fontFamily={kindredFontFamilies.sansBold}
            fontSize={11}
            lineHeight={11}
          >
            ✓
          </AppText>
        </UnsafeCheckbox.Indicator>
      </UnsafeCheckbox>
      {label || description ? (
        <UnsafeYStack flex={1} gap={4}>
          {label}
          {description ? (
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              {description}
            </AppText>
          ) : null}
        </UnsafeYStack>
      ) : null}
    </UnsafeXStack>
  );
}

type AppFormSectionProps = {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
};

export function AppFormSection({ title, description, action, children }: AppFormSectionProps) {
  return (
    <UnsafeYStack width="100%" gap={10}>
      {title || description || action ? (
        <AppSectionHeader title={title ?? ''} subtitle={description} action={action} />
      ) : null}
      {children}
    </UnsafeYStack>
  );
}

type AppFieldGroupProps = {
  label?: ReactNode;
  helper?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
};

export function AppFieldGroup({ label, helper, error, children }: AppFieldGroupProps) {
  return (
    <UnsafeYStack width="100%" gap="$xs">
      {label ? (
        <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
          {label}
        </AppText>
      ) : null}
      {children}
      {error ? (
        <AppText color="$destructive" fontSize="$sm" lineHeight="$sm">
          {error}
        </AppText>
      ) : helper ? (
        <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
          {helper}
        </AppText>
      ) : null}
    </UnsafeYStack>
  );
}

type AppSectionHeaderProps = {
  title: string;
  subtitle?: string;
  action?: ReactNode;
};

export function AppSectionHeader({ title, subtitle, action }: AppSectionHeaderProps) {
  return (
    <AppRow ai="flex-start">
      <UnsafeYStack flex={1} gap={6}>
        <AppText accessibilityRole="header" fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" lineHeight="$lg">
          {title}
        </AppText>
        {subtitle ? (
          <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
            {subtitle}
          </AppText>
        ) : null}
      </UnsafeYStack>
      {action}
    </AppRow>
  );
}

type AppInlineAlertTone = 'info' | 'success' | 'warning' | 'error';
type AppInlineAlertProps = {
  title: string;
  children: ReactNode;
  tone?: AppInlineAlertTone;
};

const ALERT_TONES: Record<AppInlineAlertTone, Partial<GetProps<typeof AppCard>>> = {
  info: {
    backgroundColor: '$secondarySoft',
    borderColor: '$borderColor',
  },
  success: {
    backgroundColor: '$accentSoft',
    borderColor: '$success',
  },
  warning: {
    backgroundColor: '$accentSoft',
    borderColor: '$accent',
  },
  error: {
    backgroundColor: '$primarySoft',
    borderColor: '$destructive',
  },
};

export function AppInlineAlert({ title, children, tone = 'info' }: AppInlineAlertProps) {
  return (
    <AppCard {...ALERT_TONES[tone]}>
      <AppCardBody py="$sm" gap="$xs">
        <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm">
          {title}
        </AppText>
        <AppText fontSize="$sm" lineHeight="$sm">
          {children}
        </AppText>
      </AppCardBody>
    </AppCard>
  );
}

type AppStateProps = {
  title: string;
  body: string;
  action?: ReactNode;
};

export function AppEmptyState({ title, body, action }: AppStateProps) {
  return (
    <AppCard>
      <AppCardBody ai="center" py="$xl">
        <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" textAlign="center">
          {title}
        </AppText>
        <AppText color="$colorMuted" textAlign="center">
          {body}
        </AppText>
        {action}
      </AppCardBody>
    </AppCard>
  );
}

export function AppLoadingState({ title, body, action }: AppStateProps) {
  return (
    <AppCard>
      <AppCardBody ai="center" py="$xl">
        <Spinner color="$primary" size="large" />
        <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" textAlign="center">
          {title}
        </AppText>
        <AppText color="$colorMuted" textAlign="center">
          {body}
        </AppText>
        {action}
      </AppCardBody>
    </AppCard>
  );
}

export function AppErrorState({ title, body, action }: AppStateProps) {
  return (
    <AppCard borderColor="$destructive">
      <AppCardBody ai="center" py="$xl">
        <AppText color="$destructive" fontFamily={kindredFontFamilies.sansBold} fontSize="$lg" textAlign="center">
          {title}
        </AppText>
        <AppText color="$colorMuted" textAlign="center">
          {body}
        </AppText>
        {action}
      </AppCardBody>
    </AppCard>
  );
}

type AppOverlaySurfaceProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  dismissible?: boolean;
  maxWidthClassName?: string;
};

function OverlayHeader({ title, description }: Pick<AppOverlaySurfaceProps, 'title' | 'description'>) {
  return (
    <UnsafeYStack gap="$xs">
      <DialogPrimitive.Title asChild>
        <AppText
          fontFamily={kindredFontFamilies.displayBold}
          fontSize="$heroSm"
          lineHeight="$heroSm"
          letterSpacing="$md"
          color="$primary"
        >
          {title}
        </AppText>
      </DialogPrimitive.Title>
      {description ? (
        <DialogPrimitive.Description asChild>
          <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
            {description}
          </AppText>
        </DialogPrimitive.Description>
      ) : null}
    </UnsafeYStack>
  );
}

export function AppDialogSurface({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  dismissible = true,
  maxWidthClassName = 'max-w-xl',
}: AppOverlaySurfaceProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[1px]" />
        <DialogPrimitive.Content
          className={`fixed left-1/2 top-1/2 z-50 w-[calc(100%-1.5rem)] -translate-x-1/2 -translate-y-1/2 outline-none ${maxWidthClassName}`}
          onEscapeKeyDown={dismissible ? undefined : (event) => event.preventDefault()}
          onPointerDownOutside={dismissible ? undefined : (event) => event.preventDefault()}
          onInteractOutside={dismissible ? undefined : (event) => event.preventDefault()}
        >
          <AppCard borderColor="$borderColor" className="rounded-[28px] border-border/80 bg-background">
            <AppCardBody px={20} py={20} gap="$md">
              <OverlayHeader title={title} description={description} />
              {children}
              {footer ? <AppButtonRow justifyContent="flex-end">{footer}</AppButtonRow> : null}
            </AppCardBody>
          </AppCard>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function AppBottomSheetSurface({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  dismissible = true,
  maxWidthClassName = 'max-w-xl',
}: AppOverlaySurfaceProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/35 backdrop-blur-[1px]" />
        <DialogPrimitive.Content
          className={`fixed inset-x-0 bottom-0 z-50 mx-auto w-full px-3 pb-0 outline-none ${maxWidthClassName}`}
          onEscapeKeyDown={dismissible ? undefined : (event) => event.preventDefault()}
          onPointerDownOutside={dismissible ? undefined : (event) => event.preventDefault()}
          onInteractOutside={dismissible ? undefined : (event) => event.preventDefault()}
        >
          <AppCard
            borderColor="$borderColor"
            className="rounded-t-[28px] rounded-b-none border-border/80 bg-background shadow-2xl"
          >
            <AppCardBody px={20} pt={20} pb={28} gap="$md">
              <OverlayHeader title={title} description={description} />
              {children}
              {footer}
            </AppCardBody>
          </AppCard>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
